import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PaymentIcon from '@mui/icons-material/Payment';
import SaveIcon from '@mui/icons-material/Save';
import SchoolIcon from '@mui/icons-material/School';
import { User } from '../../hooks/api/user';
import { beltColors, formatFullDate, getBeltLabel, getGradeLabel } from './studentGraduation';

interface StudentProfileCardProps {
  user: User;
  totalBookings: number;
  paymentLabel: string;
  paymentHelper: string;
  paymentActive: boolean;
  isAliasSaving: boolean;
  onAliasSave: (alias: string) => void;
  isInstructorSaving: boolean;
  canEditInstructor: boolean;
  onInstructorToggle: (next: boolean) => void;
}

interface StatItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  helper?: string;
}

function StatItem({ icon, label, value, helper }: StatItemProps) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ flex: 1, minWidth: 0, px: 2.5 }}
    >
      <Box sx={{ color: 'text.disabled', flexShrink: 0, display: 'flex', mt: 0.4 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.3, mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {value}
        </Typography>
        {helper && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.2, mt: 0.15 }}
          >
            {helper}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function MobileStat({
  label,
  value,
  helper,
}: {
  label: string;
  value: string | number;
  helper?: string;
}) {
  return (
    <Box sx={{ flex: 1, textAlign: 'center', px: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          lineHeight: 1.3,
          mb: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}>
        {value}
      </Typography>
      {helper && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', fontSize: 10, lineHeight: 1.2, mt: 0.2 }}
        >
          {helper}
        </Typography>
      )}
    </Box>
  );
}

export default function StudentProfileCard({
  user,
  totalBookings,
  paymentLabel,
  paymentHelper,
  paymentActive,
  isAliasSaving,
  onAliasSave,
  isInstructorSaving,
  canEditInstructor,
  onInstructorToggle,
}: StudentProfileCardProps) {
  const fullName = `${user.firstName} ${user.lastName}`;
  const beltStyle = beltColors[user.belt] ?? beltColors.white;
  const beltAccentColor = user.belt === 'white' ? '#c8c8c8' : beltStyle.background;
  const [aliasDraft, setAliasDraft] = useState(user.nickName ?? '');
  const [isEditingAlias, setIsEditingAlias] = useState(false);
  const [instructorDialogOpen, setInstructorDialogOpen] = useState(false);
  const wasInstructorSavingRef = React.useRef(isInstructorSaving);

  useEffect(() => {
    setAliasDraft(user.nickName ?? '');
  }, [user.nickName]);

  useEffect(() => {
    if (wasInstructorSavingRef.current && !isInstructorSaving) {
      setInstructorDialogOpen(false);
    }
    wasInstructorSavingRef.current = isInstructorSaving;
  }, [isInstructorSaving]);

  const handleCancelAliasEdit = () => {
    setAliasDraft(user.nickName ?? '');
    setIsEditingAlias(false);
  };

  const handleSaveAlias = () => {
    onAliasSave(aliasDraft);
    setIsEditingAlias(false);
  };

  const isInstructor = user.instructor;
  const handleInstructorChipClick = () => {
    if (!canEditInstructor || isInstructorSaving) return;
    setInstructorDialogOpen(true);
  };
  const handleConfirmInstructorToggle = () => {
    onInstructorToggle(!isInstructor);
  };

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 4, border: '1px solid rgba(18,18,18,0.08)', overflow: 'hidden' }}
    >
      <Box sx={{ height: 5, bgcolor: beltAccentColor }} />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Name + chips row */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              alt={fullName}
              src={user.image}
              sx={{
                width: { xs: 52, md: 64 },
                height: { xs: 52, md: 64 },
                fontSize: { xs: 20, md: 24 },
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {fullName}
              </Typography>
              {isEditingAlias ? (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                  <TextField
                    value={aliasDraft}
                    onChange={(event) => setAliasDraft(event.target.value)}
                    placeholder="Mote del alumno"
                    size="small"
                    sx={{ minWidth: { xs: 180, sm: 240 } }}
                  />
                  <Button
                    aria-label="Guardar alias"
                    onClick={handleSaveAlias}
                    disabled={isAliasSaving}
                    size="small"
                    variant="contained"
                    sx={{
                      minWidth: 36,
                      px: 1,
                      bgcolor: '#111111',
                      '&:hover': { bgcolor: '#2a2a2a' },
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </Button>
                  <Button
                    aria-label="Cancelar edición de alias"
                    onClick={handleCancelAliasEdit}
                    size="small"
                    sx={{ minWidth: 36, px: 1, color: '#666' }}
                  >
                    <CloseIcon fontSize="small" />
                  </Button>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                  <Typography variant="body2" color="text.secondary">
                    {user.nickName || 'Sin mote'}
                  </Typography>
                  <Button
                    aria-label="Editar alias"
                    onClick={() => setIsEditingAlias(true)}
                    size="small"
                    startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                    variant="outlined"
                    sx={{
                      minWidth: 0,
                      height: 28,
                      px: 1.25,
                      borderColor: 'rgba(17, 17, 17, 0.16)',
                      bgcolor: 'rgba(17, 17, 17, 0.02)',
                      color: '#333',
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 700,
                      '& .MuiButton-startIcon': { mr: 0.5 },
                      '&:hover': {
                        borderColor: 'rgba(17, 17, 17, 0.32)',
                        bgcolor: 'rgba(17, 17, 17, 0.06)',
                      },
                    }}
                  >
                    Editar mote
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label={`${getBeltLabel(user.belt)} · ${getGradeLabel(user.stripes)}`}
              sx={{
                bgcolor: beltStyle.background,
                color: beltStyle.color,
                border: `1px solid ${beltStyle.border}`,
                fontWeight: 700,
                fontSize: 12,
              }}
            />
            <Chip
              size="small"
              icon={<PaymentIcon sx={{ fontSize: '14px !important' }} />}
              label={paymentLabel}
              color={paymentActive ? 'success' : 'default'}
              variant={paymentActive ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 12 }}
            />
            {(isInstructor || canEditInstructor) && (
              <Chip
                size="small"
                icon={
                  isInstructorSaving ? (
                    <CircularProgress
                      size={12}
                      sx={{ color: isInstructor ? '#f8f2e7' : '#666', ml: '6px !important' }}
                    />
                  ) : (
                    <SchoolIcon sx={{ fontSize: '14px !important' }} />
                  )
                }
                label={isInstructor ? 'Instructor' : 'Marcar como instructor'}
                onClick={canEditInstructor ? handleInstructorChipClick : undefined}
                clickable={canEditInstructor}
                disabled={isInstructorSaving}
                aria-label={
                  canEditInstructor
                    ? isInstructor
                      ? `Quitar permisos de instructor a ${fullName}`
                      : `Dar permisos de instructor a ${fullName}`
                    : undefined
                }
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  bgcolor: isInstructor ? '#111111' : 'transparent',
                  color: isInstructor ? '#f8f2e7' : '#333',
                  border: isInstructor ? '1px solid #111111' : '1px dashed rgba(17,17,17,0.32)',
                  '& .MuiChip-icon': { color: 'inherit' },
                  '&:hover': canEditInstructor
                    ? {
                        bgcolor: isInstructor ? '#2a2a2a' : 'rgba(17,17,17,0.06)',
                        borderColor: isInstructor ? '#2a2a2a' : 'rgba(17,17,17,0.6)',
                      }
                    : undefined,
                }}
              />
            )}
          </Stack>
        </Stack>

        <Dialog
          open={instructorDialogOpen}
          onClose={() => !isInstructorSaving && setInstructorDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            {isInstructor ? 'Quitar permisos de instructor' : 'Dar permisos de instructor'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'text.primary' }}>
              {isInstructor ? (
                <>
                  ¿Seguro que quieres quitar los permisos de instructor a{' '}
                  <strong>{fullName}</strong>? Dejará de poder graduar alumnos, gestionar clases y
                  ver el panel de instructores.
                </>
              ) : (
                <>
                  ¿Quieres dar permisos de instructor a <strong>{fullName}</strong>? Podrá graduar
                  alumnos, crear y gestionar clases y acceder al panel de instructores.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setInstructorDialogOpen(false)}
              disabled={isInstructorSaving}
              sx={{ textTransform: 'none', color: '#666' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmInstructorToggle}
              disabled={isInstructorSaving}
              variant="contained"
              startIcon={
                isInstructorSaving ? (
                  <CircularProgress size={14} sx={{ color: '#f8f2e7' }} />
                ) : (
                  <SchoolIcon sx={{ fontSize: '16px !important' }} />
                )
              }
              sx={{
                textTransform: 'none',
                bgcolor: isInstructor ? '#b00020' : '#111111',
                '&:hover': { bgcolor: isInstructor ? '#8a0019' : '#2a2a2a' },
              }}
            >
              {isInstructor ? 'Quitar permisos' : 'Dar permisos'}
            </Button>
          </DialogActions>
        </Dialog>

        <Divider sx={{ my: { xs: 2, md: 2.5 } }} />

        {/* Mobile: compact 3-col centered grid */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
          <MobileStat label="Alta" value={formatFullDate(user.startDate)} />
          <Divider orientation="vertical" flexItem />
          <MobileStat label="Clases" value={totalBookings} helper="aprobadas" />
          <Divider orientation="vertical" flexItem />
          <MobileStat
            label="Última clase"
            value={formatFullDate(user.latestClass?.startDate)}
            helper={user.latestClass?.type}
          />
        </Box>

        {/* Desktop: horizontal strip with icons */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <StatItem
            icon={<CalendarTodayIcon sx={{ fontSize: 17 }} />}
            label="Fecha de alta"
            value={formatFullDate(user.startDate)}
          />
          <Divider orientation="vertical" flexItem />
          <StatItem
            icon={<FitnessCenterIcon sx={{ fontSize: 17 }} />}
            label="Clases asistidas"
            value={totalBookings}
            helper="solo reservas aprobadas"
          />
          <Divider orientation="vertical" flexItem />
          <StatItem
            icon={<EventAvailableIcon sx={{ fontSize: 17 }} />}
            label="Última clase"
            value={formatFullDate(user.latestClass?.startDate)}
            helper={user.latestClass?.type}
          />
          <Divider orientation="vertical" flexItem sx={{ display: { sm: 'none', md: 'block' } }} />
          <Box sx={{ display: { sm: 'none', md: 'flex' }, flex: 1 }}>
            <StatItem
              icon={<PaymentIcon sx={{ fontSize: 17 }} />}
              label="Cuota"
              value={paymentLabel}
              helper={paymentHelper}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
